/** @jsxRuntime classic */
import "./polyfills";
import "./styles/index.scss";
import "../node_modules/@q4/nimbus-ui/dist/_styles.css";
import "../node_modules/@q4/nimbus-ui/dist/v1/_mixins.scss";
import "../node_modules/@q4/nimbus-ui/dist/v1/_variables.scss";
import { Auth0Provider } from "@auth0/auth0-react";
import loadable from "@loadable/component";
import { ConfigProvider as NimbusConfigProvider, StyleGuide, ErrorLogProvider, LoadingScreen } from "@q4/nimbus-ui";
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  RowStyleModule,
  RowSelectionModule,
  PinnedRowModule,
  CellStyleModule,
  TooltipModule,
  ColumnAutoSizeModule,
  RowApiModule,
} from "ag-grid-community";
import {
  ContextMenuModule,
  MenuModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ValidationModule,
  LicenseManager,
  ColumnApiModule,
} from "ag-grid-enterprise";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import config, { Environment } from "./config";
import {
  LSAuthenticatedProvider,
  NavigationProvider,
  BanklistProvider,
  GlobalMessageProvider,
  ReportProvider,
  TickerProvider,
  FilingProvider,
  HomeProvider,
  DTCCProvider,
  RefetchProvider,
  ManagerProvider,
  MutualFundProvider,
  CustodianProvider,
  FundProvider,
} from "./contexts";
import * as serviceWorker from "./serviceWorker";
import Fallback from "./views/root/components/fallback/fallback.component";
import Root from "./views/root/root.view";

LicenseManager.setLicenseKey(config.agGridKey);

const LDProvider = loadable(() => asyncWithLDProvider(config.LDConfig));
const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  MenuModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ContextMenuModule,
  RowStyleModule,
  RowSelectionModule,
  PinnedRowModule,
  ValidationModule,
  CellStyleModule,
  TooltipModule,
  ColumnAutoSizeModule,
  ColumnApiModule,
  RowApiModule,
]);
const root = createRoot(rootEl);
/*
 * Ticker provider has to wrap the other view providers,
 * because some of the other fetch calls are dependent on a default ticker being present
 */
root.render(
  <LDProvider fallback={<LoadingScreen />}>
    <Auth0Provider
      {...config.auth0}
      authorizationParams={{ redirect_uri: window.location.origin, audience: config.auth0.audience }}
    >
      <ErrorLogProvider
        bugsnagConfig={{
          apiKey: config.errorLog.apiKey,
          collectUserIp: false,
          enabledReleaseStages: [Environment.Develop, Environment.Production],
          releaseStage: config.env as Environment,
          appVersion: config.version,
        }}
        FallbackComponent={Fallback}
      >
        <GlobalMessageProvider>
          <LSAuthenticatedProvider>
            <Router>
              <NimbusConfigProvider styleGuide={StyleGuide.V1}>
                <NavigationProvider>
                  <RefetchProvider>
                    <TickerProvider>
                      <HomeProvider>
                        <DTCCProvider>
                          <ReportProvider>
                            <FilingProvider>
                              <BanklistProvider>
                                <MutualFundProvider>
                                  <ManagerProvider>
                                    <CustodianProvider>
                                      <FundProvider>
                                        <Root />
                                      </FundProvider>
                                    </CustodianProvider>
                                  </ManagerProvider>
                                </MutualFundProvider>
                              </BanklistProvider>
                            </FilingProvider>
                          </ReportProvider>
                        </DTCCProvider>
                      </HomeProvider>
                    </TickerProvider>
                  </RefetchProvider>
                </NavigationProvider>
              </NimbusConfigProvider>
            </Router>
          </LSAuthenticatedProvider>
        </GlobalMessageProvider>
      </ErrorLogProvider>
    </Auth0Provider>
  </LDProvider>
);
document.getElementById("root");
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
